// React context for analytics.
//
// Why a context (and not a module-level singleton)?
//   - Storybook / tests mount isolated clients.
//   - SSR needs a fresh client per request to avoid cross-tenant bleed.

import { createContext, createElement, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AnalyticsClient } from '../shared/types.js';

const AnalyticsContext = createContext<AnalyticsClient | null>(null);
AnalyticsContext.displayName = 'GovardAnalyticsContext';

export interface AnalyticsProviderProps {
  client: AnalyticsClient;
  children?: ReactNode;
}

export function AnalyticsProvider(props: AnalyticsProviderProps): ReactNode {
  // `client` is already a stable identity from useMemo in the caller — we
  // still memoize the context value to be defensive about re-renders.
  const value = useMemo(() => props.client, [props.client]);
  return createElement(AnalyticsContext.Provider, { value }, props.children);
}

/**
 * Returns the closest analytics client. Throws if no provider is mounted —
 * that's a wiring bug and it should fail loudly in dev.
 */
export function useAnalytics(): AnalyticsClient {
  const client = useContext(AnalyticsContext);
  if (!client) {
    throw new Error('[@govard/analytics] useAnalytics must be used within <AnalyticsProvider>.');
  }
  return client;
}

/**
 * Fires `page()` on mount with the current `window.location`. Apps are free to
 * wire their router's route-change event to `analytics.page(url)` instead —
 * this hook is a convenience for SPAs that don't need that granularity.
 */
export function usePageView(): void {
  const analytics = useAnalytics();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    analytics.page(window.location.href, document.referrer || undefined);
  }, [analytics]);
}

export { AnalyticsContext };
